import { Switch, Route, useLocation, useSearch, Redirect } from "wouter";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { getStoredLanguage, setStoredLanguage, type AppLanguage } from "@/i18n";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery, useMutation } from "@tanstack/react-query";
import { useActivityTracker } from "@/hooks/use-activity-tracker";
import { startTelemetryBeacon } from "@/lib/telemetry-beacon";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar, type SidebarSection } from "@/components/app-sidebar";
import { DraggableDean } from "@/components/draggable-dean";
import { ThemeProvider, useTheme } from "@/components/theme-provider";
import { DeanProvider } from "@/contexts/dean-context";
import { ScannerProvider } from "@/contexts/scanner-context";
import { useScannerContext } from "@/contexts/use-scanner-context";
import { toast as fireToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Sun, Moon, RefreshCw, AlertTriangle, X, FileText, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CURRENT_TOS_VERSION } from "@shared/constants";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { useImpersonation } from "@/hooks/use-impersonation";
import { ErrorBoundary } from "@/components/error-boundary";
import LoginPage from "@/pages/login";
import TermsPage from "@/pages/terms";
import PrivacyPage from "@/pages/privacy";
import LandingPage from "@/pages/landing";
import DashboardPage from "@/pages/dashboard";
import HubPage from "@/pages/hub";
import CfoHubPage from "@/pages/cfo-hub";
import CooHubPage from "@/pages/coo-hub";
import DealershipHubPage from "@/pages/dealership-hub";
import FloorplanPage from "@/pages/floorplan";
import OperationsPage from "@/pages/operations";
import SlaPage from "@/pages/sla";
import AdminPage from "@/pages/admin";
import SubscribePage from "@/pages/subscribe";
import ResetPasswordPage from "@/pages/reset-password";
import AcceptInvitePage from "@/pages/accept-invite";
import AccountPage from "@/pages/account";
import SalespersonPage from "@/pages/salesperson";
import SalespersonDealerView from "@/pages/salesperson-dealer-view";
import FloorplanInventoryPage from "@/pages/floorplan-inventory";
import InventoryPage from "@/pages/inventory";
import OnboardingPage from "@/pages/onboarding";
import VendorDirectoryPage from "@/pages/vendor-directory";
import BankHealthPage from "@/pages/bank-health";
import PotentialSavingsPage from "@/pages/potential-savings";
import DocumentsPage from "@/pages/documents";
import FinancialStatementsPage from "@/pages/financial-statements";
import AnalyticsPage from "@/pages/analytics";
import DeanChatsPage from "@/pages/dean-chats";
import DealerSocialPage from "@/pages/dealer-social";
import SuspendedPage from "@/pages/suspended";
import CanceledPage from "@/pages/canceled";
import NotFound from "@/pages/not-found";
import MarketplacePage from "@/pages/marketplace";
import MarketplaceDetailPage from "@/pages/marketplace-detail";
import MarketplaceListingFormPage from "@/pages/marketplace-listing-form";
import MarketplaceMessagesPage from "@/pages/marketplace-messages";
import MarketplaceSettingsPage from "@/pages/marketplace-settings";
import { MarketplaceProfileGate, MarketplaceIntroPopup } from "@/components/marketplace-profile-gate";
import { TermsGate, CURRENT_TERMS_VERSION } from "@/components/terms-gate";

export const ONBOARDING_DISMISSED_KEY = "onboarding_dismissed";

// ─── Floating Scan Pill ────────────────────────────────────────────────────────

function FloatingScanPill() {
  const { t } = useTranslation(["nav", "common"]);
  const scanner = useScannerContext();
  const [, navigate] = useLocation();
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const prevStatus = useRef(scanner.status);

  // Fire toast when scan transitions to completed or failed
  useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = scanner.status;

    if (!scanner.minimized) return;

    if (prev === "scanning" && scanner.status === "completed") {
      fireToast({
        title: t("nav:scanPill.completeTitle"),
        description: t("nav:scanPill.completeDesc"),
      });
    }

    if (prev === "scanning" && scanner.status === "failed") {
      fireToast({
        title: t("nav:scanPill.failedTitle"),
        description: scanner.errorMessage || t("nav:scanPill.failedDesc"),
        variant: "destructive",
        action: (
          <ToastAction
            altText={t("nav:scanPill.tryAgain")}
            onClick={() => {
              scanner.requestReopen();
              navigate(scanner.originPath);
            }}
          >
            {t("nav:scanPill.tryAgain")}
          </ToastAction>
        ),
      });
    }
  }, [scanner.status]);

  // Only show pill when minimized, not dismissed, and a scan is active
  if (!user) return null;
  if (!scanner.minimized) return null;
  if (scanner.dismissed) return null;
  if (scanner.status === "idle") return null;

  const isFailed = scanner.status === "failed";
  const isCompleted = scanner.status === "completed";
  const isScanning = scanner.status === "scanning";

  const handleClick = () => {
    scanner.requestReopen();
    navigate(scanner.originPath);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    scanner.dismiss();
  };

  const progressLabel = isScanning && scanner.progressStep
    ? scanner.progressStep
    : isFailed
    ? t("nav:scanPill.failedRetry")
    : isCompleted
    ? t("nav:scanPill.completeReview")
    : t("nav:scanPill.scanning");

  const tooltipText = isScanning && scanner.progressPct != null
    ? `${progressLabel} (${scanner.progressPct}%)`
    : progressLabel;

  return (
    <div
      data-testid="floating-scan-pill"
      title={tooltipText}
      className={`
        fixed bottom-6 left-20 z-50 flex flex-col overflow-hidden rounded-full shadow-lg
        text-sm font-medium transition-all border
        ${isFailed
          ? "bg-destructive text-destructive-foreground border-destructive/50"
          : isCompleted
          ? "bg-chart-3 text-white border-chart-3/50 animate-pulse"
          : "bg-background text-foreground border-border"
        }
      `}
    >
      <span className={`flex items-center gap-2.5 pl-4 py-2.5 ${isCompleted ? "pr-4" : "pr-1"}`}>
        <button
          onClick={handleClick}
          className="flex items-center gap-2.5 cursor-pointer hover:opacity-80"
          data-testid="scan-pill-main-button"
        >
          {isFailed ? (
            <AlertTriangle className="w-4 h-4 shrink-0" />
          ) : isCompleted ? (
            <RefreshCw className="w-4 h-4 shrink-0" />
          ) : (
            <RefreshCw className="w-4 h-4 shrink-0 animate-spin" />
          )}
          <span data-testid="scan-pill-label">{progressLabel}</span>
          {isScanning && scanner.progressPct != null && (
            <span data-testid="scan-pill-pct" className="text-xs opacity-70 tabular-nums">
              {scanner.progressPct}%
            </span>
          )}
        </button>
        {!isCompleted && (
          <button
            onClick={handleDismiss}
            data-testid="scan-pill-dismiss"
            title={t("nav:scanPill.dismiss")}
            className={`ml-1 p-1 rounded-full cursor-pointer opacity-60 hover:opacity-100 transition-opacity shrink-0 ${
              isFailed
                ? "hover:bg-destructive-foreground/10"
                : "hover:bg-muted"
            }`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
      {isScanning && scanner.progressPct != null && (
        <span
          data-testid="scan-pill-progress-bar"
          className="block h-0.5 bg-primary/40 transition-all duration-500"
          style={{ width: `${scanner.progressPct}%` }}
        />
      )}
    </div>
  );
}

// ─── Scan Completion Redirect ──────────────────────────────────────────────────
// When a backgrounded receivable scan completes, this takes the dealer straight
// back to the normal review experience — the same Document Reader box and
// "Save to History" flow used for foreground scans (or the Add Lender review
// dialog for that flow). Nothing is ever silently saved in the background.

function ScanCompletionRedirect() {
  const scanner = useScannerContext();
  const [, navigate] = useLocation();
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const handledJobRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) return;
    if (scanner.status !== "completed" || !scanner.minimized) return;
    if (!scanner.result) return;
    if (!scanner.jobId || handledJobRef.current === scanner.jobId) return;
    handledJobRef.current = scanner.jobId;
    // Pop the standard review UI: navigate back to where the scan started and
    // request reopen — the floorplan page opens the Document Reader (or the Add
    // Lender review dialog) exactly as it would for a foreground scan.
    scanner.requestReopen();
    navigate(scanner.originPath);
  }, [user, scanner.status, scanner.minimized, scanner.result, scanner.jobId]);

  return null;
}

// ─── Env Badge ─────────────────────────────────────────────────────────────────

function EnvBadge() {
  const isProd = import.meta.env.PROD;
  const prodUrl = import.meta.env.VITE_PROD_URL as string | undefined;
  const devUrl = import.meta.env.VITE_DEV_URL as string | undefined;
  const label = isProd ? "PROD" : "DEV";
  const counterpartUrl = isProd ? (devUrl || null) : (prodUrl || null);

  const badge = (
    <Badge
      data-testid="badge-env"
      className={`text-xs font-mono px-2 py-0.5 select-none ${
        isProd
          ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700"
          : "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700"
      }`}
      variant="outline"
    >
      {label}
    </Badge>
  );

  if (counterpartUrl) {
    return (
      <a
        href={counterpartUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="link-env-switch"
        title={`Switch to ${isProd ? "DEV" : "PROD"}`}
      >
        {badge}
      </a>
    );
  }

  return badge;
}

// ─── Theme Toggle ──────────────────────────────────────────────────────────────

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <Button
      size="icon"
      variant="ghost"
      data-testid="button-theme-toggle"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
    >
      {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </Button>
  );
}

function TosGate({ user, children }: { user: any; children: React.ReactNode }) {
  const { t } = useTranslation(["nav", "common"]);
  const [agreed, setAgreed] = useState(false);
  const needsAcceptance =
    (user.role === "subscriber" || user.role === "demo") &&
    user.tosVersion !== CURRENT_TOS_VERSION;

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/auth/accept-tos", { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Failed to record acceptance");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
  });

  if (!needsAcceptance) return <>{children}</>;

  return (
    <>
      {children}
      <Dialog open modal>
        <DialogContent className="sm:max-w-lg" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {t("nav:tos.title")}
            </DialogTitle>
            <DialogDescription>
              {t("nav:tos.description", { version: CURRENT_TOS_VERSION })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary underline-offset-4 hover:underline"
              data-testid="link-tos-review"
            >
              <FileText className="w-4 h-4" />
              {t("nav:tos.reviewLink")}
            </a>
            <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
              <Checkbox
                id="tos-accept-checkbox"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(v === true)}
                data-testid="checkbox-tos-accept"
              />
              <Label htmlFor="tos-accept-checkbox" className="text-sm leading-relaxed cursor-pointer">
                {t("nav:tos.agree", { version: CURRENT_TOS_VERSION })}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={!agreed || acceptMutation.isPending}
              onClick={() => acceptMutation.mutate()}
              className="w-full gap-2"
              data-testid="button-tos-accept-submit"
            >
              {acceptMutation.isPending ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              {acceptMutation.isPending ? t("nav:tos.saving") : t("nav:tos.agreeContinue")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AuthGuard({ children, allowSalesperson = false }: { children: React.ReactNode; allowSalesperson?: boolean }) {
  const { t } = useTranslation(["nav", "common"]);
  const { data: user, isLoading, isError } = useQuery<any>({
    queryKey: ["/api/auth/me"],
    retry: false,
  });
  const [location] = useLocation();

  // Only the anonymous root is public. Render without waiting for auth so
  // reviewers can read the business/SMS information even if that request stalls.
  if (location === "/" && (!user || isError)) {
    return <LandingPage />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">{t("nav:loading")}</p>
        </div>
      </div>
    );
  }

  if ((!user || isError) && location !== "/login") {
    return <Redirect to="/login" />;
  }

  if (user && !isError && location === "/login") {
    if (user.accountStatus === "suspended" || user.subscriptionStatus === "canceled") return <Redirect to="/suspended" />;
    if (user.role === "salesperson") return <Redirect to="/salesperson" />;
    return <Redirect to="/" />;
  }

  // Suspended or canceled dealers may only access the /suspended self-service page
  const isBlocked = user?.accountStatus === "suspended" || user?.subscriptionStatus === "canceled";
  if (user && !isError && isBlocked && location !== "/suspended") {
    return <Redirect to="/suspended" />;
  }
  if (user && !isError && !isBlocked && location === "/suspended") {
    return <Redirect to="/" />;
  }

  if (user && !isError) {
    const isSalesperson = user.role === "salesperson";
    const hasAccess = user.role === "owner" || user.role === "demo" || isSalesperson || user.subscriptionStatus === "active" || user.subscriptionStatus === "trialing";

    if (!hasAccess && location !== "/subscribe") {
      return <Redirect to="/subscribe" />;
    }
    if (hasAccess && location === "/subscribe") {
      if (isSalesperson) return <Redirect to="/salesperson" />;
      return <Redirect to="/" />;
    }
    if (isSalesperson && !allowSalesperson) {
      return <Redirect to="/salesperson" />;
    }
    if (!isSalesperson && allowSalesperson) {
      return <Redirect to="/" />;
    }

    // Terms gate — skip for admin/owner/salesperson/demo roles since ToS applies to dealers
    const requiresTermsAcceptance =
      user.role === "subscriber" &&
      user.tosVersion !== CURRENT_TERMS_VERSION;
    if (requiresTermsAcceptance) {
      return <TermsGate onAccepted={() => {}} />;
    }

    const needsOnboarding =
      (user.role === "subscriber" || user.role === "demo") &&
      !user.onboardingCompleted &&
      location !== "/onboarding" &&
      location !== "/subscribe";
    if (needsOnboarding) {
      return <Redirect to="/onboarding" />;
    }
  }

  if (user && !isError) {
    return <TosGate user={user}>{children}</TosGate>;
  }

  return <>{children}</>;
}

function detectSection(path: string, search: string): SidebarSection {
  const params = new URLSearchParams(search);
  const tab = params.get("tab") ?? "";

  if (path === "/cfo") return "cfo";
  if (path === "/coo") return "coo";
  if (path === "/dealership") return "dealership";

  const cfoDeepLinks = ["/floorplan", "/financial-statements", "/bank-health", "/potential-savings"];
  const cfoOpsTabs = ["analytics", "expenses", "pldata"];
  if (cfoDeepLinks.includes(path)) return "cfo";
  if (path === "/operations" && (tab === "" || cfoOpsTabs.includes(tab))) return "cfo";

  const cooDeepLinks = ["/floorplan-inventory", "/inventory", "/vendor-directory", "/marketplace"];
  const cooOpsTabs = ["marketing", "team"];
  if (cooDeepLinks.includes(path)) return "coo";
  if (path.startsWith("/marketplace")) return "coo";
  if (path === "/operations" && cooOpsTabs.includes(tab)) return "coo";

  const dealershipDeepLinks = ["/sla", "/documents", "/dean", "/dealer-social"];
  const dealershipOpsTabs = ["assessment", "performance-assessment"];
  if (dealershipDeepLinks.includes(path)) return "dealership";
  if (path === "/operations" && dealershipOpsTabs.includes(tab)) return "dealership";

  return null;
}

function AppLayout({ children, section }: { children: React.ReactNode; section?: SidebarSection }) {
  useActivityTracker();
  const { isViewOnly } = useImpersonation();
  const { data: layoutUser } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });

  // Client telemetry beacon — only once a user is authenticated (endpoint requires auth).
  useEffect(() => {
    if (layoutUser?.id) startTelemetryBeacon();
  }, [layoutUser?.id]);

  const sidebarStyle = {
    "--sidebar-width": "15rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-hidden">
        <AppSidebar section={section} />
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <ImpersonationBanner />
          <header className="flex items-center justify-between px-4 py-2 border-b bg-background sticky top-0 z-50">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <div className="flex items-center gap-2">
              <EnvBadge />
              <ThemeToggle />
            </div>
          </header>
          <main
            className="flex-1 overflow-auto"
            data-view-only={isViewOnly ? "true" : undefined}
            data-testid={isViewOnly ? "main-view-only" : undefined}
          >
            <ErrorBoundary label="Page">
              {children}
            </ErrorBoundary>
          </main>
        </div>
      </div>
      <MarketplaceIntroPopup user={layoutUser} />
    </SidebarProvider>
  );
}

function AdminRouteGuard({ renderPage }: { renderPage?: React.ReactNode } = {}) {
  const { data: user, isLoading } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user?.role === "salesperson") return <Redirect to="/salesperson" />;
  if (user?.role !== "admin" && user?.role !== "owner") return <Redirect to="/" />;
  return renderPage ? <>{renderPage}</> : <AdminPage />;
}

function Router() {
  const [location] = useLocation();
  const search = useSearch();

  if (location === "/login") {
    return (
      <AuthGuard>
        <LoginPage />
      </AuthGuard>
    );
  }

  if (location.startsWith("/reset-password")) {
    return <ResetPasswordPage />;
  }

  if (location.startsWith("/accept-invite")) {
    return <AcceptInvitePage />;
  }

  if (location === "/terms") {
    return <TermsPage />;
  }

  if (location === "/privacy") {
    return <PrivacyPage />;
  }

  if (location === "/canceled") {
    return <CanceledPage />;
  }

  if (location === "/suspended") {
    return (
      <AuthGuard>
        <SuspendedPage />
      </AuthGuard>
    );
  }

  if (location === "/admin") {
    return <AdminRouteGuard />;
  }

  if (location === "/salesperson") {
    return (
      <AuthGuard allowSalesperson>
        <SalespersonPage />
      </AuthGuard>
    );
  }

  if (location.startsWith("/salesperson/dealer/")) {
    return (
      <AuthGuard allowSalesperson>
        <SalespersonDealerView />
      </AuthGuard>
    );
  }

  if (location.startsWith("/subscribe")) {
    return (
      <AuthGuard>
        <SubscribePage />
      </AuthGuard>
    );
  }

  if (location === "/onboarding") {
    return (
      <AuthGuard>
        <OnboardingPage />
      </AuthGuard>
    );
  }

  if (location === "/" || location === "/hub") {
    return (
      <AuthGuard>
        <AppLayout section={null}>
          <HubPage />
        </AppLayout>
      </AuthGuard>
    );
  }

  const section = detectSection(location, search);

  return (
    <AuthGuard>
      <AppLayout section={section}>
        <Switch>
          {/* Hub aliases — already handled above but kept for Switch completeness */}
          <Route path="/" component={HubPage} />
          <Route path="/hub" component={HubPage} />

          {/* Section hub pages */}
          <Route path="/cfo" component={CfoHubPage} />
          <Route path="/coo" component={CooHubPage} />
          <Route path="/dealership" component={DealershipHubPage} />

          {/* Assessment shortcut from hub button */}
          <Route path="/dealership/assessment">{() => <Redirect to="/operations?tab=assessment" />}</Route>

          {/* All existing pages — preserved for DONUT revert */}
          <Route path="/dashboard" component={DashboardPage} />
          <Route path="/floorplan" component={FloorplanPage} />
          <Route path="/floorplan-inventory" component={FloorplanInventoryPage} />
          <Route path="/inventory" component={InventoryPage} />
          <Route path="/operations" component={OperationsPage} />
          <Route path="/goals">{() => <Redirect to="/sla" />}</Route>
          <Route path="/sla" component={SlaPage} />
          <Route path="/ai">{() => <Redirect to="/" />}</Route>
          <Route path="/account" component={AccountPage} />
          <Route path="/vendor-directory" component={VendorDirectoryPage} />
          <Route path="/bank-health" component={BankHealthPage} />
          <Route path="/potential-savings" component={PotentialSavingsPage} />
          <Route path="/documents" component={DocumentsPage} />
          <Route path="/financial-statements" component={FinancialStatementsPage} />
          <Route path="/analytics" component={AnalyticsPage} />
          <Route path="/dean" component={DeanChatsPage} />
          <Route path="/dealer-social" component={DealerSocialPage} />
          <Route path="/admin/ai-intelligence">{() => <Redirect to="/admin" />}</Route>

          {/* Marketplace — all listing/browse/messaging routes require a complete
              marketplace profile. Settings stays open so the dealer can build it. */}
          <Route path="/marketplace/settings" component={MarketplaceSettingsPage} />
          <Route path="/marketplace">
            {() => <MarketplaceProfileGate><MarketplacePage /></MarketplaceProfileGate>}
          </Route>
          <Route path="/marketplace/messages">
            {() => <MarketplaceProfileGate><MarketplaceMessagesPage /></MarketplaceProfileGate>}
          </Route>
          <Route path="/marketplace/new">
            {() => <MarketplaceProfileGate><MarketplaceListingFormPage /></MarketplaceProfileGate>}
          </Route>
          <Route path="/marketplace/:id/edit">
            {() => <MarketplaceProfileGate><MarketplaceListingFormPage /></MarketplaceProfileGate>}
          </Route>
          <Route path="/marketplace/:id">
            {() => <MarketplaceProfileGate><MarketplaceDetailPage /></MarketplaceProfileGate>}
          </Route>

          <Route component={NotFound} />
        </Switch>
      </AppLayout>
    </AuthGuard>
  );
}

function GlobalDeanCompanion() {
  const { data: user, isLoading } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const [location] = useLocation();

  const unauthenticatedPaths = ["/login", "/reset-password", "/accept-invite", "/canceled"];
  const isUnauthenticated = !user || isLoading;
  const isPublicPath = unauthenticatedPaths.some((p) => location.startsWith(p));

  if (isUnauthenticated || isPublicPath) return null;

  return (
    <ErrorBoundary label="DEAN Companion">
      <DraggableDean />
    </ErrorBoundary>
  );
}

// Keeps the active i18n language in sync with the authenticated user's stored
// preference. Falls back to localStorage (set at init) before /api/auth/me loads.
function LanguageSync() {
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  useEffect(() => {
    const lang: AppLanguage = user?.language === "es" ? "es" : "en";
    if (user && i18n.language !== lang) {
      i18n.changeLanguage(lang);
      setStoredLanguage(lang);
    }
  }, [user?.language, user]);
  useEffect(() => {
    const stored = getStoredLanguage();
    if (i18n.language !== stored) i18n.changeLanguage(stored);
  }, []);
  return null;
}

function App() {
  useEffect(() => {
    const prevent = (e: WheelEvent) => {
      const el = e.target as HTMLInputElement;
      if (el.tagName === "INPUT" && el.type === "number" && document.activeElement === el) {
        e.preventDefault();
        el.blur();
      }
    };
    document.addEventListener("wheel", prevent, { passive: false });
    return () => document.removeEventListener("wheel", prevent);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ScannerProvider>
          <DeanProvider>
            <TooltipProvider delayDuration={0}>
              <Toaster />
              <LanguageSync />
              <ErrorBoundary label="App">
                <Router />
              </ErrorBoundary>
              <GlobalDeanCompanion />
              <FloatingScanPill />
              <ScanCompletionRedirect />
            </TooltipProvider>
          </DeanProvider>
        </ScannerProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
